import React from "react";
import { CheckCircle, XCircle, MapPin, Ruler } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

function formatPrice(price) {
  if (price >= 10000000) return `\u20B9${(price / 10000000).toFixed(1)} Cr`;
  if (price >= 100000) return `\u20B9${(price / 100000).toFixed(1)} Lakh`;
  return `\u20B9${price.toLocaleString("en-IN")}`;
}

export default function PlotCard({ plot, onEnquire }) {
  const { t } = useLang();
  const isAvailable = plot.status === "available";
  const isReserved = plot.status === "reserved";

  return (
    <div
      className="plot-card overflow-hidden flex flex-col group"
      data-testid={`plot-card-${plot.size}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={plot.image_url || "https://images.unsplash.com/photo-1668301952339-53ba65165d2e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"}
          alt={`${plot.size} sqmtr industrial plot`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1668301952339-53ba65165d2e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
          }}
        />
        <div
          className={`absolute top-3 left-3 px-2.5 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1 ${
            isAvailable
              ? "bg-emerald-500 text-white"
              : isReserved
              ? "bg-amber-500 text-white"
              : "bg-slate-700 text-white"
          }`}
          data-testid={`plot-status-${plot.size}`}
        >
          {isAvailable ? (
            <CheckCircle className="w-3 h-3 pulse-available" />
          ) : (
            <XCircle className="w-3 h-3" />
          )}
          {plot.status.charAt(0).toUpperCase() + plot.status.slice(1)}
        </div>
        <div className="absolute top-3 right-3 bg-slate-950/90 text-white px-2.5 py-1 text-sm font-bold border border-white/10">
          <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {plot.size.toLocaleString("en-IN")}
          </span>
          <span className="text-xs ml-1 font-normal text-slate-400">SQ.MTR</span>
        </div>
        {/* Hover overlay with data */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
          <div className="flex items-center gap-1.5 text-xs text-slate-300">
            <MapPin className="w-3.5 h-3.5 text-orange-400" />
            IIE Sigaddi, SIIDCUL, Kotdwar
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1 bg-[#1E293B]">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs text-slate-500 mb-0.5">{t("plotCard.pricePerSqmtr")}</div>
            <div
              className="text-2xl font-bold text-orange-500"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
              data-testid={`plot-price-${plot.size}`}
            >
              {`\u20B9${plot.price_per_sqmtr?.toLocaleString("en-IN")}`}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 mb-0.5">{t("plotCard.totalValue")}</div>
            <div className="text-sm font-bold text-white">
              {formatPrice(plot.total_price)}
            </div>
          </div>
        </div>

        {plot.description && (
          <p className="text-sm text-slate-400 leading-relaxed mb-3 line-clamp-2">
            {plot.description}
          </p>
        )}

        {plot.features?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {plot.features.slice(0, 3).map((f) => (
              <span
                key={f}
                className="bg-white/5 text-slate-400 text-xs px-2 py-0.5 border border-white/10"
              >
                {f}
              </span>
            ))}
            {plot.features.length > 3 && (
              <span className="bg-white/5 text-slate-500 text-xs px-2 py-0.5 border border-white/10">
                +{plot.features.length - 3} {t("plotCard.more")}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-4">
          <Ruler className="w-3.5 h-3.5" />
          {t("plotCard.area")}: {plot.size.toLocaleString("en-IN")} sq.mtr ({(plot.size / 10000).toFixed(4)} {t("plotCard.hectare")})
        </div>

        <div className="mt-auto">
          <button
            onClick={() => onEnquire && onEnquire(plot)}
            disabled={!isAvailable}
            data-testid={`plot-enquire-btn-${plot.size}`}
            className={`w-full py-2.5 text-sm font-bold uppercase tracking-wider transition-all duration-300 ${
              isAvailable
                ? "bg-orange-500 hover:bg-orange-600 text-white hover:shadow-[0_0_20px_rgba(249,115,22,0.3)]"
                : "bg-white/5 text-slate-600 cursor-not-allowed border border-white/10"
            }`}
          >
            {isAvailable ? t("plotCard.enquireNow") : t("plotCard.notAvailable")}
          </button>
        </div>
      </div>
    </div>
  );
}
